<template>
  <div class="landings" :class="{ _expanded: expanded }">
    <div class="section" :class="{ invalid }">
      <div v-if="!expanded" class="landings__head">
        <h3>{{ $t("platform.arbitrage.choose_landing") }}</h3>
        <p class="max-390">
          {{ $t("platform.arbitrage.if_more_than_1_landing") }}
        </p>
      </div>

      <div class="landings-selected">
        <div class="landings-selected__title">{{ $t("platform.card.landings.selected", { count: value.length, total: landings.length }) }}</div>
        <ul v-if="selected.length" class="landings-selected__list">
          <li v-for="land in selected" :key="land.id" class="landings-selected__item">
            <img :src="land.image" alt="" class="landings-selected__thumb" />
            <span class="landings-selected__name">{{ landName(land) }}</span>
            <button type="button" class="landings-selected__remove" :title="$t('platform.card.landings.remove')" :aria-label="$t('platform.card.landings.remove')" @click="toggle(land)">
              <SatIcon name="x" :size="14" />
            </button>
          </li>
        </ul>
        <p v-else class="landings-selected__empty">{{ $t("platform.card.landings.none") }}</p>
      </div>

      <Tabs class="land-tabs" :tabs="tabs" :currentTab="currentTab" @setTab="currentTab = $event" />
      <div class="landings__list mt-3">
        <div
          class="landings__item landing"
          :class="{
            list: order === 'list',
            grid: order === 'grid',
            active: value.includes(land.id),
          }"
          v-for="land in filteredLandings"
          :key="land.id"
          @click="toggle(land)"
        >
          <div class="landing__thumb thumb">
            <img :src="`${land.image}`" alt="" />
          </div>
          <div class="landing__body">
            <div class="landing__title">
              {{ landName(land) }}
            </div>
            <div class="landing__footer">
              <button @click.stop.prevent="openLandModal(land)" class="btn icon-view">
                {{ $t("platform.arbitrage.watch") }}
              </button>

              <label class="checkbox-landing">
                <svg
                  class="checkbox-landing__globe"
                  v-tooltip="{
                    html: true,
                    content: `
                      <span class='landing-tooltip__icon'>
                        <svg viewBox='0 0 32 32' width='17' height='17' xmlns='http://www.w3.org/2000/svg'>
                          <path d='M14.4 24h3.2v-9.6h-3.2v9.6zM16 0c-8.84 0-16 7.16-16 16s7.16 16 16 16c8.84 0 16-7.16 16-16s-7.16-16-16-16zM16 28.8c-7.056 0-12.8-5.744-12.8-12.8s5.744-12.8 12.8-12.8c7.056 0 12.8 5.744 12.8 12.8s-5.744 12.8-12.8 12.8zM14.4 11.2h3.2v-3.2h-3.2v3.2z'></path>
                        </svg>
                      </span>
                      <span>${$t('platform.arbitrage.global')}</span>`,
                    triggers: ['hover'],
                    class: 'landing-tooltip',
                    distance: '10',
                  }"
                  v-svg
                  symbol="globe"
                  role="info"
                ></svg>
                <button type="button" @click.stop.prevent="toggle(land)">
                  <svg class="checkbox-landing__icon" v-svg symbol="check-land" size="0 0 21 21" role="info"></svg>
                </button>
                <span class="checkbox-landing__border"></span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <ul class="landings__view">
        <li>
          <button class="btn _icon _view" :class="{ active: order === 'grid' }" @click.prevent="order = 'grid'">
            <svg v-svg symbol="grid" size="0 0 14 14" role="info"></svg>
          </button>
        </li>
        <li>
          <button class="btn _icon _view" :class="{ active: order === 'list' }" @click.prevent="order = 'list'">
            <svg v-svg symbol="list" size="0 0 12 12" role="info"></svg>
          </button>
        </li>
      </ul>
    </div>
    <modal @closed="clearLandingModal" :width="780" name="landModal" height="auto" :scrollable="true" classes="creative-modal" :shiftY="0">
      <LandingModal :landing="currentLanding" @closeModal="closeLandingModal" />
    </modal>
  </div>
</template>
<script>
import "@/assets/css/_page-add-platform.scss";
import "@/assets/css/_section.scss";
import Tabs from "@/components/statictics/Tabs.vue";
import LandingModal from "@/components/LandingModal";
import LandingMixin from "@/mixins/landModal";
import SatIcon from "@/components/Satellites/SatIcon.vue";

// The landing pages an arbitrage platform sends its traffic to. v-model is the list of landing ids. The chosen ones
// are repeated on top as thumbnails. `expanded`: the whole list on the page (the platform card), not a scroll box.
export default {
  name: "LandingsPicker",
  components: { Tabs, LandingModal, SatIcon },
  mixins: [LandingMixin],
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    landings: {
      type: Array,
      default: () => [],
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tabs: ["all", "adult", "mainstream"],
      currentTab: "all",
      order: "grid",
    };
  },
  computed: {
    // In the order they were picked; ids of landings that are gone are skipped
    selected() {
      return this.value.map((id) => this.landings.find((land) => land.id === id)).filter(Boolean);
    },
    filteredLandings() {
      if (this.currentTab === "adult") return this.landings.filter((el) => el.category == 1);
      if (this.currentTab === "mainstream") return this.landings.filter((el) => el.category == 2);
      return this.landings;
    },
  },
  methods: {
    landName(land) {
      return this.$i18n.locale === "ru" ? land.name_ru : land.name_en;
    },
    toggle(land) {
      this.$emit("input", this.value.includes(land.id) ? this.value.filter((id) => id !== land.id) : [...this.value, land.id]);
    },
  },
};
</script>
<style lang="scss" scoped>
.land-tabs ::v-deep li {
  width: 33%;
}

.landings-selected {
  margin-bottom: 20px;
}

.landings-selected__title {
  margin-bottom: 10px;
  font-size: 15px;
  font-weight: 600;
}

.landings-selected__list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.landings-selected__item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  padding: 4px 4px 4px 4px;
  border: 1px solid #37807d;
  border-radius: 10px;
  background: #fff;
}

.landings-selected__thumb {
  flex-shrink: 0;
  width: 56px;
  height: 30px;
  border-radius: 6px;
  object-fit: cover;
  background: #f4f4f4;
}

.landings-selected__name {
  overflow: hidden;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.landings-selected__remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  color: #9b9ba4;
  background: transparent;
  cursor: pointer;

  &:hover {
    color: #d23c3c;
    background: #fdecec;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px #37807d;
  }
}

.landings-selected__empty {
  margin: 0;
  font-size: 13px;
  color: #9b9ba4;
}

// On the platform card the list takes the page: no own frame, no inner scroll, more columns
.landings._expanded {
  padding: 0;

  .section {
    padding: 0;
    border-radius: 0;
    background: transparent;

    &.invalid {
      border: 0;
    }
  }

  .landings__view {
    top: 0;
    right: 0;
  }

  .landings-selected {
    padding-right: 90px;
  }

  .landings__list {
    max-height: none;
    overflow: visible;
  }

  .landing {
    width: calc(25% - 14px);
    border-color: #eeeef3;

    &.active {
      border-color: #37807d;
    }

    &.list {
      width: calc(100% - 14px);
    }

    @media screen and (max-width: 1500px) {
      width: calc(33.3333% - 14px);
    }

    @media screen and (max-width: 991px) {
      width: calc(50% - 14px);
    }

    @media screen and (max-width: 450px) {
      width: calc(100% - 14px);
    }
  }

  @media screen and (max-width: 767px) {
    margin-left: 0;
    margin-right: 0;
    width: 100%;
  }
}

body.dark {
  .landings-selected__item {
    background: #262629;
  }

  .landings-selected__thumb {
    background: #151515;
  }

  .landings._expanded .landing:not(.active) {
    border-color: #343438;
  }
}
</style>
